// Human-readable clause summaries — the English shown on a predicate's editable button (operator +
// value, or "…" when unset). The column `type` (from the leaf's Row) selects the operator label +
// shape; `summarizeRaw` is the fallback for a leaf with no Row (unknown type → non-editable).

import dayjs from 'dayjs';

import type { AttributePredicate, AttributeType, ConditionValue, OperatorDef, ValueShape } from '../types';
import {
  asBoolean,
  asDate,
  asDateRange,
  asNumber,
  asNumberRange,
  asText,
  asWindow,
  ATTRIBUTE_OPERATORS,
  findOperator,
  operatorDef,
} from './operators';

/** The placeholder shown for an empty value. */
export const EMPTY_VALUE = '…';

const formatDate = (d: string): string => {
  const day = dayjs(d);
  return day.isValid() ? day.format('MMM D, YYYY') : d;
};

/** The value part of a clause summary, or `null` when it's empty (→ the "…" placeholder). */
function summarizeValue(shape: ValueShape, value: ConditionValue): string | null {
  switch (shape) {
    case 'none':
      return null;
    case 'text': {
      const s = asText(value);
      return s || null;
    }
    case 'single_enum': {
      // Usually a string, but a valid operator on the wrong/unknown type (read-only fallback) may carry a
      // number or boolean — render it rather than dropping it to "…".
      if (typeof value === 'string') return value || null;
      return typeof value === 'number' || typeof value === 'boolean' ? String(value) : null;
    }
    case 'multi_enum': {
      // string[] (enum) or number[] (numeric `in`) — join whatever members are present.
      if (!Array.isArray(value)) return null;
      const members = value.filter((v): v is string | number => v != null);
      return members.length ? members.map(String).join(', ') : null;
    }
    case 'number': {
      const n = asNumber(value);
      return n != null ? String(n) : null;
    }
    case 'number_range': {
      const [lo, hi] = asNumberRange(value);
      return lo != null || hi != null ? `${lo ?? EMPTY_VALUE} and ${hi ?? EMPTY_VALUE}` : null;
    }
    case 'boolean': {
      const b = asBoolean(value);
      return b === undefined ? null : b ? 'true' : 'false';
    }
    case 'date': {
      const d = asDate(value);
      return d ? formatDate(d) : null;
    }
    case 'date_range': {
      const [start, end] = asDateRange(value);
      if (!start && !end) return null;
      return `${start ? formatDate(start) : EMPTY_VALUE} and ${end ? formatDate(end) : EMPTY_VALUE}`;
    }
    case 'relative_window': {
      const w = asWindow(value);
      return w && Number.isFinite(w.interval) ? `${w.interval} ${w.unit}` : null;
    }
  }
}

/** An operator label and its value part, kept SEPARATE so the row can render the operator muted
 *  (grey) and the value in the foreground. `value` is null for a presence op; an empty value → "…". */
export interface ClauseSummary {
  operator: string;
  value: string | null;
}

function clause(label: string, shape: ValueShape, value: ConditionValue): ClauseSummary {
  if (shape === 'none') return { operator: label, value: null };
  return { operator: label, value: summarizeValue(shape, value) ?? EMPTY_VALUE };
}

/** An attribute predicate's clause, given its column type — "is one of Enterprise, Pro" / "is set". */
export function summarizeAttribute(predicate: AttributePredicate, type: AttributeType): ClauseSummary {
  // Prefer the type's own operator label; if the operator isn't in that type's set (a read-only leaf),
  // fall back to its type-free label + shape so it still reads as English, not the raw operator key.
  const def = findOperator(ATTRIBUTE_OPERATORS[type], predicate.operator) ?? operatorDef(predicate.operator);
  return summarizeWithDef(def, predicate.operator, predicate.value ?? null);
}

/** Type-free fallback for a leaf with no Row — a valid operator still gets its label + value shape; a
 *  truly unknown operator shows the raw name + a best-effort value string. */
export function summarizeRaw(predicate: AttributePredicate): ClauseSummary {
  return summarizeWithDef(operatorDef(predicate.operator), predicate.operator, predicate.value ?? null);
}

/** Format a clause from a resolved operator def (label + shape); no def ⇒ raw operator + best-effort value. */
function summarizeWithDef(def: OperatorDef | undefined, operator: string, value: ConditionValue): ClauseSummary {
  if (def) return clause(def.label, def.shape, value);
  if (value == null) return { operator, value: null };
  if (Array.isArray(value)) {
    const parts = value.map((v) => (v == null ? EMPTY_VALUE : String(v)));
    return { operator, value: parts.length ? parts.join(', ') : null };
  }
  // RelativeWindow (the only object-shaped value) → "30 days"; primitives stringify directly.
  const text = typeof value === 'object' ? `${value.interval} ${value.unit}` : String(value);
  return { operator, value: text || null };
}
